import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, Users, AlertTriangle, Calendar,
  RefreshCw, Crown, Phone, ExternalLink, Send, Clock, BarChart3, Wallet,
} from 'lucide-react';
import { getBackendUrl } from './utils/api';

const API = `${getBackendUrl()}/api`;

const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (v) => Number(v || 0).toLocaleString('pt-BR');

const FinancialMetricsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/master/financial-metrics?months=6`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar métricas');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendReminderWhatsApp = (store) => {
    const phone = (store.phone || '').replace(/\D/g, '');
    if (!phone) {
      toast.error(`${store.name} não tem WhatsApp cadastrado`);
      return;
    }
    const phoneFull = phone.startsWith('55') ? phone : `55${phone}`;
    const days = store.days_to_expire;
    const dayText = days === 0 ? 'HOJE' : days === 1 ? 'amanhã' : `em ${days} dias`;
    const msg = encodeURIComponent(
      `Olá! 👋\n\n` +
      `Sua mensalidade do Pede é Fácil vence *${dayText}*.\n\n` +
      `💰 Valor: ${brl(store.price)}\n` +
      `📅 Vencimento: ${new Date(store.expiry_date).toLocaleDateString('pt-BR')}\n\n` +
      `Renove agora para continuar recebendo pedidos sem interrupção! 🚀`
    );
    window.open(`https://wa.me/${phoneFull}?text=${msg}`, '_blank');
  };

  if (loading) {
    return (
      <div className="glass-card p-12 text-center text-gray-400">
        <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin" />
        Carregando métricas financeiras...
      </div>
    );
  }
  if (!data) return null;

  const churnRisk = data.mrr_at_risk_7d > 0;
  const growthUp = data.growth_pct >= 0;
  const maxGrowth = Math.max(1, ...data.growth_history.map(h => h.new_restaurants));

  return (
    <div className="space-y-6" data-testid="financial-metrics-panel">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-emerald-400" />
            Financeiro & MRR
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Receita recorrente do SaaS · lojas suspensas não entram nas métricas
          </p>
        </div>
        <button
          data-testid="financial-refresh"
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 text-sm border border-white/10 disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* HERO METRICS - 4 cards principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* MRR */}
        <div className="relative rounded-2xl bg-gradient-to-br from-emerald-600/20 via-emerald-700/10 to-transparent border border-emerald-500/30 p-5 overflow-hidden">
          <div className="absolute -right-4 -top-4 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-emerald-300 font-semibold">MRR</span>
              <DollarSign className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="text-3xl md:text-4xl font-black text-white tracking-tight" data-testid="metric-mrr">
              {brl(data.mrr)}
            </div>
            <p className="text-xs text-gray-400 mt-1">Receita mensal recorrente</p>
            <p className="text-[11px] text-emerald-300/80 mt-2">
              {data.paying_active_count} {data.paying_active_count === 1 ? 'loja pagante' : 'lojas pagantes'}
            </p>
          </div>
        </div>

        {/* ARR */}
        <div className="relative rounded-2xl bg-gradient-to-br from-blue-600/20 via-cyan-700/10 to-transparent border border-blue-500/30 p-5 overflow-hidden">
          <div className="absolute -right-4 -top-4 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-blue-300 font-semibold">ARR</span>
              <BarChart3 className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-3xl md:text-4xl font-black text-white tracking-tight" data-testid="metric-arr">
              {brl(data.arr)}
            </div>
            <p className="text-xs text-gray-400 mt-1">Receita anual projetada</p>
            <p className="text-[11px] text-blue-300/80 mt-2">MRR × 12 meses</p>
          </div>
        </div>

        {/* Ticket médio */}
        <div className="relative rounded-2xl bg-gradient-to-br from-violet-600/20 via-purple-700/10 to-transparent border border-violet-500/30 p-5 overflow-hidden">
          <div className="absolute -right-4 -top-4 w-24 h-24 bg-violet-500/10 rounded-full blur-2xl"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-violet-300 font-semibold">Ticket Médio</span>
              <Wallet className="w-5 h-5 text-violet-400" />
            </div>
            <div className="text-3xl md:text-4xl font-black text-white tracking-tight" data-testid="metric-ticket">
              {brl(data.average_ticket)}
            </div>
            <p className="text-xs text-gray-400 mt-1">Por loja pagante</p>
            <p className="text-[11px] text-violet-300/80 mt-2">Padrão: {brl(data.default_price)}</p>
          </div>
        </div>

        {/* Crescimento */}
        <div className={`relative rounded-2xl bg-gradient-to-br ${
          growthUp ? 'from-amber-600/20 via-orange-700/10' : 'from-rose-600/20 via-red-700/10'
        } to-transparent border ${growthUp ? 'border-amber-500/30' : 'border-rose-500/30'} p-5 overflow-hidden`}>
          <div className={`absolute -right-4 -top-4 w-24 h-24 ${growthUp ? 'bg-amber-500/10' : 'bg-rose-500/10'} rounded-full blur-2xl`}></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs uppercase tracking-wider ${growthUp ? 'text-amber-300' : 'text-rose-300'} font-semibold`}>Crescimento</span>
              {growthUp ? <TrendingUp className="w-5 h-5 text-amber-400" /> : <TrendingDown className="w-5 h-5 text-rose-400" />}
            </div>
            <div className="text-3xl md:text-4xl font-black text-white tracking-tight" data-testid="metric-growth">
              {growthUp ? '+' : ''}{data.growth_pct}%
            </div>
            <p className="text-xs text-gray-400 mt-1">vs mês anterior</p>
            <p className={`text-[11px] mt-2 ${growthUp ? 'text-amber-300/80' : 'text-rose-300/80'}`}>
              {data.new_this_month} novas este mês · {data.new_last_month} no passado
            </p>
          </div>
        </div>
      </div>

      {/* SECOND ROW - Métricas de risco/saúde */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
          <div className="flex items-center gap-2 text-amber-300 mb-1">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-wide font-semibold">Vencendo 7d</span>
          </div>
          <p className="text-2xl font-bold text-white" data-testid="metric-expiring-7d">{data.expiring_7d_count}</p>
          <p className="text-xs text-amber-200/70 mt-0.5">{brl(data.mrr_at_risk_7d)} em risco</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
          <div className="flex items-center gap-2 text-yellow-300 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-wide font-semibold">Vencendo 15d</span>
          </div>
          <p className="text-2xl font-bold text-white">{data.expiring_15d_count}</p>
          <p className="text-xs text-yellow-200/70 mt-0.5">monitorar de perto</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
          <div className="flex items-center gap-2 text-rose-300 mb-1">
            <TrendingDown className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-wide font-semibold">Expiradas</span>
          </div>
          <p className="text-2xl font-bold text-white" data-testid="metric-expired">{data.expired_count}</p>
          <p className="text-xs text-rose-200/70 mt-0.5">{brl(data.mrr_lost)} perdidos</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
          <div className="flex items-center gap-2 text-slate-400 mb-1">
            <Users className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-wide font-semibold">Suspensas</span>
          </div>
          <p className="text-2xl font-bold text-white">{data.suspended_count}</p>
          <p className="text-xs text-slate-400 mt-0.5">não conta no MRR</p>
        </div>
      </div>

      {/* TWO COLUMN LAYOUT */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Lista de vencendo - ações acionáveis */}
        <div className="xl:col-span-2 glass-card overflow-hidden" data-testid="expiring-list-section">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h3 className="text-white font-bold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Renovações Próximas
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {data.expiring_list.length === 0 ? 'Tudo em dia!' : `${data.expiring_list.length} lojas exigem atenção`}
              </p>
            </div>
            <span className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded-full">
              {brl(data.mrr_at_risk_7d)} em risco
            </span>
          </div>

          {data.expiring_list.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center mb-3">
                <Calendar className="w-8 h-8 text-emerald-400" />
              </div>
              <p className="text-gray-300">Nenhuma loja vencendo nos próximos 15 dias 🎉</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
              {data.expiring_list.map((s) => {
                const isUrgent = s.days_to_expire <= 3;
                return (
                  <div key={s.id} data-testid={`expiring-row-${s.id}`} className="p-4 flex flex-wrap items-center gap-3 hover:bg-white/[0.02]">
                    <div className={`w-12 h-12 rounded-lg ${isUrgent ? 'bg-rose-500/15 border-rose-500/40' : 'bg-amber-500/15 border-amber-500/40'} border flex items-center justify-center font-bold text-white flex-shrink-0`}>
                      <Calendar className={`w-5 h-5 ${isUrgent ? 'text-rose-300' : 'text-amber-300'}`} />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                      <p className="font-semibold text-white text-sm">{s.name}</p>
                      <p className="text-xs text-gray-400">{s.subdomain ? `${s.subdomain} · ` : ''}{s.email}</p>
                    </div>
                    <div className="text-center min-w-[80px]">
                      <p className={`text-lg font-bold ${isUrgent ? 'text-rose-300' : 'text-amber-300'}`}>
                        {s.days_to_expire === 0 ? 'HOJE' : s.days_to_expire === 1 ? '1 dia' : `${s.days_to_expire} dias`}
                      </p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">vence em</p>
                    </div>
                    <div className="text-right min-w-[80px]">
                      <p className="font-bold text-emerald-300">{brl(s.price)}</p>
                      <p className="text-[10px] text-gray-500">mensalidade</p>
                    </div>
                    <button
                      data-testid={`reminder-btn-${s.id}`}
                      onClick={() => sendReminderWhatsApp(s)}
                      disabled={!s.phone}
                      title={s.phone ? 'Enviar lembrete via WhatsApp' : 'Sem telefone cadastrado'}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600/15 hover:bg-emerald-600/30 text-emerald-300 text-xs font-medium border border-emerald-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Cobrar
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sidebar direita */}
        <div className="space-y-4">
          {/* Top 10 que mais pagam */}
          <div className="glass-card overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-400" />
                Top Receita
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">Lojas que mais contribuem</p>
            </div>
            <div className="divide-y divide-white/5 max-h-[300px] overflow-y-auto">
              {data.top_paying.length === 0 ? (
                <p className="p-4 text-xs text-gray-400 text-center">Sem lojas pagantes</p>
              ) : data.top_paying.map((t, i) => (
                <div key={t.id} className="p-3 flex items-center gap-3 hover:bg-white/[0.02]">
                  <span className={`text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    i === 0 ? 'bg-yellow-500/20 text-yellow-300' :
                    i === 1 ? 'bg-slate-400/20 text-slate-300' :
                    i === 2 ? 'bg-orange-500/20 text-orange-300' :
                    'bg-white/5 text-gray-400'
                  }`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{t.name}</p>
                    {t.is_custom && <p className="text-[10px] text-violet-300">preço customizado</p>}
                  </div>
                  <p className="text-sm font-bold text-emerald-300">{brl(t.price)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Distribuição de preços */}
          <div className="glass-card overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-white font-bold text-sm">Distribuição de Preços</h3>
              <p className="text-xs text-gray-400 mt-0.5">Lojas por faixa de mensalidade</p>
            </div>
            <div className="p-4 space-y-2">
              {Object.entries(data.price_buckets).map(([bucket, count]) => {
                const max = Math.max(1, ...Object.values(data.price_buckets));
                const pct = (count / max) * 100;
                return (
                  <div key={bucket}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-300">{bucket}</span>
                      <span className="text-gray-400 font-medium">{count}</span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-green-400 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Crescimento histórico */}
      <div className="glass-card p-5">
        <h3 className="text-white font-bold flex items-center gap-2 mb-1">
          <TrendingUp className="w-5 h-5 text-emerald-400" />
          Histórico de Novas Lojas
        </h3>
        <p className="text-xs text-gray-400 mb-4">Últimos {data.growth_history.length} meses</p>
        <div className="flex items-end justify-between gap-2 h-32">
          {data.growth_history.map((h) => {
            const pct = (h.new_restaurants / maxGrowth) * 100;
            return (
              <div key={h.month} className="flex-1 flex flex-col items-center gap-1.5">
                <span className="text-xs text-white font-semibold">{h.new_restaurants}</span>
                <div className="w-full flex-1 bg-white/5 rounded-md overflow-hidden flex items-end">
                  <div
                    className="w-full bg-gradient-to-t from-emerald-600 to-green-400 transition-all rounded-md min-h-[2px]"
                    style={{ height: `${Math.max(2, pct)}%` }}
                  />
                </div>
                <span className="text-[10px] text-gray-400 uppercase">{h.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Expiradas (recuperação) */}
      {data.expired_list.length > 0 && (
        <div className="glass-card overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h3 className="text-white font-bold flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-rose-400" />
                Lojas Expiradas — Oportunidade de Recuperação
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {data.expired_list.length} lojas · até {brl(data.mrr_lost)} de receita recuperável
              </p>
            </div>
          </div>
          <div className="divide-y divide-white/5 max-h-[300px] overflow-y-auto">
            {data.expired_list.map((s) => (
              <div key={s.id} className="p-3 flex items-center gap-3 hover:bg-white/[0.02]">
                <div className="w-10 h-10 rounded-lg bg-rose-500/15 border border-rose-500/40 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 text-rose-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{s.name}</p>
                  <p className="text-xs text-gray-500">{s.subdomain}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-rose-300 font-medium">{s.days_overdue} dia(s) atrasada</p>
                  <p className="text-xs text-gray-400">{brl(s.price)}</p>
                </div>
                {s.phone && (
                  <a
                    href={`https://wa.me/${(s.phone || '').replace(/\D/g, '').replace(/^0+/, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 rounded-lg bg-emerald-600/15 hover:bg-emerald-600/30 text-emerald-300"
                    title="Falar via WhatsApp"
                  >
                    <Phone className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialMetricsPanel;
